/*
 * Entry point.
 *
 * 1. Run capability gates before loading anything heavy (spec sec. 4, sec. 9).
 * 2. If the environment is supported, boot the game and drive the DOM screen
 *    flow from its state: loading -> intro -> playing <-> paused -> defeat.
 */
import { checkEnvironment } from "./core/capabilities.js";
import { SETTINGS_RANGES } from "./config.js";
import {
  showScreen,
  hideAllScreens,
  mountControls,
  setLoadingProgress,
  setDefeatReason,
  setUnsupportedReason,
} from "./ui/screens.js";
import { startScrollMedia, stopScrollMedia } from "./ui/scroll.js";
import { startLoreMedia, stopLoreMedia } from "./ui/lore.js";

/** Wire the pause-screen comfort settings to the store. */
function wireSettings(settings) {
  const invert = document.getElementById("set-invert-y");
  if (invert) {
    invert.checked = settings.get("invertLookY");
    invert.addEventListener("change", () =>
      settings.set("invertLookY", invert.checked),
    );
  }

  const slider = (id, key) => {
    const input = document.getElementById(id);
    const out = document.getElementById(`${id}-val`);
    if (!input) return;
    const range = SETTINGS_RANGES[key];
    input.min = String(range.min);
    input.max = String(range.max);
    input.step = String(range.step);
    const sync = (v) => {
      if (out) out.textContent = `${Number(v).toFixed(2)}×`;
    };
    input.value = String(settings.get(key));
    sync(input.value);
    input.addEventListener("input", () => {
      settings.set(key, input.value);
      sync(input.value);
    });
  };
  slider("set-look-sens", "lookSensitivity");
  slider("set-turn-speed", "turnSpeed");
}

/** Wire the pause-screen audio on/off button (spec sec. 9, sec. 10). */
function wireAudioButton(game) {
  const btn = document.getElementById("btn-audio");
  if (!btn) return;
  const sync = (on) => {
    btn.textContent = `Audio: ${on ? "On" : "Off"}`;
    btn.setAttribute("aria-pressed", String(on));
  };
  sync(game.audio.enabled);
  game.audio.onChange = sync;
  btn.addEventListener("click", () => game.setAudioEnabled(!game.audio.enabled));
}

async function boot() {
  const env = checkEnvironment();
  if (!env.ok) {
    if (env.reason) setUnsupportedReason(env.reason);
    showScreen(env.screen);
    return;
  }

  const canvas = document.getElementById("game-canvas");
  if (!(canvas instanceof HTMLCanvasElement)) {
    setUnsupportedReason("The game canvas is missing from the page.");
    showScreen("unsupported");
    return;
  }

  mountControls();
  setLoadingProgress(0, "Starting");

  try {
    const { Game, GameState } = await import("./core/Game.js");
    // `?spawn=<portal>` (e.g. ?spawn=mario) — a deep link / return trip from a
    // portal destination: skip the intro and spawn at that portal (World.load).
    // `?character=<id>` picks a hero from src/player/characters.js.
    const params = new URLSearchParams(window.location.search);
    const spawn = params.get("spawn");
    const character = params.get("character");
    const game = new Game(canvas, { spawn, character });

    game.onProgress = (fraction, label) => setLoadingProgress(fraction, label);

    game.onStateChange = (state) => {
      // Halt the looping altar video whenever we leave (or never entered) the
      // scroll, so it does not keep playing behind the game.
      if (state !== GameState.SCROLL) stopScrollMedia();
      if (state !== GameState.LORE) stopLoreMedia();

      if (state === GameState.RUNNING) {
        if (game.awaitingStart) {
          showScreen("intro");
          document.getElementById("btn-begin")?.focus();
        } else {
          hideAllScreens();
        }
      } else if (state === GameState.PAUSED) {
        showScreen("pause");
      } else if (state === GameState.DEFEAT) {
        setDefeatReason(game.defeatCause);
        showScreen("defeat");
      } else if (state === GameState.SCROLL) {
        showScreen("scroll");
        startScrollMedia();
        document.getElementById("btn-scroll-close")?.focus();
      } else if (state === GameState.LORE) {
        const screen = game.loreScreen;
        showScreen(`lore-${screen}`);
        startLoreMedia(screen);
        document
          .querySelector(`#screen-lore-${screen} [data-lore-close]`)
          ?.focus();
      } else if (state === GameState.PORTAL) {
        showScreen("portal");
      }
      // LOADING: leave the loading screen up until play begins.
    };

    // Stepping into a mushroom portal is a one-way trip: show the transition
    // screen (via onStateChange, above), hold a beat so it reads, then navigate
    // the browser to the other dimension (URL_ASTRAMARIO).
    game.onPortal = (url) => {
      window.setTimeout(() => window.location.assign(url), 750);
    };

    const on = (id, fn) =>
      document.getElementById(id)?.addEventListener("click", fn);

    document
      .getElementById("screen-intro")
      ?.addEventListener("mousedown", () => game.beginFromIntro());
    on("btn-begin", () => game.beginFromIntro());
    on("btn-resume", () => game.beginPlay());
    on("btn-pause-restart", () => game.restart());
    on("btn-restart", () => game.restart());
    on("btn-pause-title", () => game.returnToTitle());
    on("btn-defeat-title", () => game.returnToTitle());
    on("btn-scroll-close", () => game.closeScroll());

    // Clicking the dark margin around the parchment also closes the scroll.
    document.getElementById("screen-scroll")?.addEventListener("mousedown", (e) => {
      if (e.target === e.currentTarget) game.closeScroll();
    });

    // Every hologram-lore-altar overlay closes the same way: its [data-lore-close]
    // button, or a click on the dark margin. Generic so a new #screen-lore-<id>
    // block needs no wiring here.
    for (const root of document.querySelectorAll('[id^="screen-lore-"]')) {
      root
        .querySelector("[data-lore-close]")
        ?.addEventListener("click", () => game.closeLore());
      root.addEventListener("mousedown", (e) => {
        if (e.target === e.currentTarget) game.closeLore();
      });
    }

    wireSettings(game.settings);
    wireAudioButton(game);

    await game.start();

    // Expose for manual inspection during development.
    window.__game = game;
  } catch (err) {
    console.error("[boot] failed to start", err);
    setUnsupportedReason(
      "Something went wrong while starting the prototype. Check the console.",
    );
    showScreen("unsupported");
  }
}

boot();
